import { useState } from "react";
import { Button } from "@/components/ui/button";
import MedicalKnowledgeDisplay from "@/components/results/MedicalKnowledgeDisplay";
import { MedicalKnowledgeEntry } from "@/services/medicalKnowledge";
import { BookOpen, FileText, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

interface EvidenceSectionProps {
  relevantResearch: MedicalKnowledgeEntry[];
  clinicalGuidelines: MedicalKnowledgeEntry[];
  drugSafetyInfo: MedicalKnowledgeEntry[];
}

const EvidenceSection = ({ 
  relevantResearch, 
  clinicalGuidelines, 
  drugSafetyInfo 
}: EvidenceSectionProps) => {
  const [showAllResearch, setShowAllResearch] = useState(false);
  const [showAllGuidelines, setShowAllGuidelines] = useState(false);
  
  const researchToShow = showAllResearch ? relevantResearch : relevantResearch.slice(0, 2);
  const guidelinesToShow = showAllGuidelines ? clinicalGuidelines : clinicalGuidelines.slice(0, 2);

  return (
    <>
      {/* Evidence-Based Literature Review */}
      {relevantResearch.length > 0 && (
        <div className="space-y-4">
          <MedicalKnowledgeDisplay
            title="Evidence-Based Literature Review"
            entries={researchToShow}
            icon={BookOpen}
            color="green"
            maxDisplay={showAllResearch ? relevantResearch.length : 2}
            showFullDetails={true}
          />
          
          {relevantResearch.length > 2 && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => setShowAllResearch(!showAllResearch)}
                className="flex items-center space-x-2"
              >
                {showAllResearch ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    <span>Show Less Literature</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    <span>Show All Literature ({relevantResearch.length - 2} more)</span>
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Clinical Guidelines */}
      {clinicalGuidelines.length > 0 && (
        <div className="space-y-4">
          <MedicalKnowledgeDisplay
            title="Clinical Guidelines & Protocols"
            entries={guidelinesToShow}
            icon={FileText}
            color="purple"
            maxDisplay={showAllGuidelines ? clinicalGuidelines.length : 2}
            showFullDetails={true}
          />
          
          {clinicalGuidelines.length > 2 && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => setShowAllGuidelines(!showAllGuidelines)}
                className="flex items-center space-x-2"
              >
                {showAllGuidelines ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    <span>Show Less Guidelines</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    <span>Show All Guidelines ({clinicalGuidelines.length - 2} more)</span>
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Drug Safety Information */}
      {drugSafetyInfo.length > 0 && (
        <MedicalKnowledgeDisplay
          title="Drug Safety Information"
          entries={drugSafetyInfo}
          icon={AlertTriangle}
          color="orange"
          maxDisplay={4}
          showFullDetails={false}
        />
      )}
    </>
  );
};

export default EvidenceSection;